import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import { FilterDropdown } from '../components/common/FilterDropdown';
import { SiteTable } from '../components/sites/SiteTable';
import { BulkNemAnalysisDialog } from '../components/sites/BulkNemAnalysisDialog';
import { BulkSalesforceRefreshDialog } from '../components/sites/BulkSalesforceRefreshDialog';
import { siteService } from '../services/siteService';
import type { SiteListSortField } from '../services/siteService';
import type { BvSite } from '../types/billValidationTypes';

// Selection toolbar + checkboxes are shared by both bulk actions below.
const BULK_ACTIONS_ENABLED = true;
// NEM Analysis bulk action stays hidden for now (was disabled before this
// change for unrelated reasons) — only the new Salesforce refresh action is enabled.
const BULK_NEM_ENABLED = false;

const STATUS_OPTIONS = ['Enrolled', 'Pending Enrolment', 'Not Enrolled', 'Pending Cancellation', 'Cancelled'];
const PAGE_SIZE = 50;


export function SiteListPage() {
  const navigate = useNavigate();
  const [sites, setSites] = useState<BvSite[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [distributorFilter, setDistributorFilter] = useState('');
  const [retailerFilter, setRetailerFilter] = useState('');
  const [tariffFilter, setTariffFilter] = useState('');
  const [meterFilter, setMeterFilter] = useState('');

  const [sortField, setSortField] = useState<SiteListSortField>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  const [options, setOptions] = useState<{
    state: string[]; city: string[]; distributor: string[];
    tariff: string[]; meter: string[]; retailer: string[];
  }>({ state: [], city: [], distributor: [], tariff: [], meter: [], retailer: [] });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic request counter — see the note in `load`.
  const latestRequestRef = useRef(0);

  // Bulk selection — kept as a Map so full BvSite objects (nmi/org_id/client
  // name) are available to the bulk dialog without a re-fetch. Persists across
  // page-forward/back within the same filter set; cleared whenever the
  // underlying dataset changes (filters/search/sort) via resetPage().
  const [selected, setSelected] = useState<Map<string, BvSite>>(new Map());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSfOpen, setBulkSfOpen] = useState(false);

  const load = useCallback(async () => {
    // Debouncing only cancels the pending timer, not a request already in
    // flight. Two overlapping queries can resolve out of order, so a slower
    // earlier one used to overwrite the newer results — the list visibly
    // reverted to matches for a prefix of what had been typed. Only the newest
    // request is allowed to write state.
    const requestId = ++latestRequestRef.current;
    setLoading(true);
    const result = await siteService.list({
      search: search.trim() || undefined,
      status: statusFilter || undefined,
      state: stateFilter || undefined,
      city: cityFilter || undefined,
      distributor: distributorFilter || undefined,
      retailer: retailerFilter || undefined,
      tariff: tariffFilter || undefined,
      meter: meterFilter || undefined,
      sort: sortField,
      sortDir,
      page,
      size: PAGE_SIZE,
    });
    if (requestId !== latestRequestRef.current) return;
    if (result.success) {
      setSites(result.data?.sites ?? []);
      setTotal(result.data?.total ?? 0);
    }
    setLoading(false);
  }, [search, statusFilter, stateFilter, cityFilter, distributorFilter,
      retailerFilter, tariffFilter, meterFilter, sortField, sortDir, page]);

  // Debounce: search typed → wait; filter/sort/page change → immediate.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { load(); }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [load]);

  // Distinct dropdown options, RLS-scoped, one call on mount.
  useEffect(() => {
    (async () => {
      const r = await siteService.listFilterOptions();
      if (r.success && r.data) setOptions(r.data);
    })();
  }, []);

  const hasActiveFilters = search || statusFilter || stateFilter || cityFilter
    || distributorFilter || retailerFilter || tariffFilter || meterFilter;

  // Any filter/search/sort change resets to page 1 and clears the selection —
  // a different dataset makes a carried-over selection ambiguous/stale.
  const resetPage = () => { setPage(1); setSelected(new Map()); };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else {
        const site = sites.find((s) => s.id === id);
        if (site) next.set(id, site);
      }
      return next;
    });
  };

  const togglePage = (ids: string[], checked: boolean) => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const id of ids) {
        if (checked) {
          const site = sites.find((s) => s.id === id);
          if (site) next.set(id, site);
        } else {
          next.delete(id);
        }
      }
      return next;
    });
  };

  const onSearch = (v: string) => { setSearch(v); resetPage(); };
  const onFilter = (setter: (v: string) => void) => (v: string) => { setter(v); resetPage(); };

  const onSort = (field: SiteListSortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    resetPage();
  };

  const clearAllFilters = () => {
    setSearch(''); setStatusFilter(''); setStateFilter(''); setCityFilter('');
    setDistributorFilter(''); setRetailerFilter(''); setTariffFilter(''); setMeterFilter('');
    resetPage();
  };

  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="h-full flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Sites</h1>
          <p className="text-sm text-gray-500 mt-0.5">All NMI sites across entities</p>
        </div>
        <Button onClick={() => navigate('/bill-validation/sites/new')}>
          <Plus className="h-4 w-4 mr-1.5" />
          New Site
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            className="pl-8 h-9 w-52 text-sm"
            placeholder="Search NMI or entity…"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
          />
        </div>
        <FilterDropdown label="State"       value={stateFilter}       onChange={onFilter(setStateFilter)}       options={options.state} />
        <FilterDropdown label="City"        value={cityFilter}        onChange={onFilter(setCityFilter)}        options={options.city} />
        <FilterDropdown label="Distributor" value={distributorFilter} onChange={onFilter(setDistributorFilter)} options={options.distributor} />
        <FilterDropdown label="Retailer"    value={retailerFilter}    onChange={onFilter(setRetailerFilter)}    options={options.retailer} />
        <FilterDropdown label="Tariff"      value={tariffFilter}      onChange={onFilter(setTariffFilter)}      options={options.tariff} />
        <FilterDropdown label="Meter"       value={meterFilter}       onChange={onFilter(setMeterFilter)}       options={options.meter} />
        <FilterDropdown label="Status"      value={statusFilter}      onChange={onFilter(setStatusFilter)}      options={STATUS_OPTIONS} />
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" className="h-9 text-xs text-gray-500 gap-1" onClick={clearAllFilters}>
            <X className="h-3.5 w-3.5" />
            Clear filters
          </Button>
        )}
      </div>

      {BULK_ACTIONS_ENABLED && selected.size > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-3.5 py-2">
          <p className="text-sm text-blue-800">
            <span className="font-semibold">{selected.size}</span> site{selected.size === 1 ? '' : 's'} selected
          </p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Map())}>Clear</Button>
            <Button variant={BULK_NEM_ENABLED ? 'outline' : 'default'} size="sm" onClick={() => setBulkSfOpen(true)}>Refresh from Salesforce</Button>
            {BULK_NEM_ENABLED && (
              <Button size="sm" onClick={() => setBulkOpen(true)}>Run NEM Analysis</Button>
            )}
          </div>
        </div>
      )}

      <div className="flex-1 bg-white rounded-xl border border-gray-200">
        <SiteTable
          sites={sites}
          loading={loading}
          sortField={sortField}
          sortDir={sortDir}
          onSort={onSort}
          onSelect={(s) => navigate(`/bill-validation/sites/${s.id}`)}
          onOpenNewWindow={(s) => window.open(`/bill-validation/sites/${s.id}`, '_blank', 'noopener,noreferrer')}
          {...(BULK_ACTIONS_ENABLED ? {
            selectedIds: new Set(selected.keys()),
            onToggleOne: toggleOne,
            onTogglePage: togglePage,
          } : {})}
        />
      </div>

      <div className="flex items-center justify-between px-1 text-xs text-gray-500">
        <span>
          {loading ? '…' : `${total} site${total === 1 ? '' : 's'}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span>Page {page} of {lastPage}</span>
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2"
            disabled={page >= lastPage || loading}
            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {BULK_ACTIONS_ENABLED && (
        <>
          {BULK_NEM_ENABLED && (
            <BulkNemAnalysisDialog
              open={bulkOpen}
              onClose={() => setBulkOpen(false)}
              sites={[...selected.values()]}
            />
          )}
          <BulkSalesforceRefreshDialog
            open={bulkSfOpen}
            onClose={() => setBulkSfOpen(false)}
            sites={[...selected.values()]}
          />
        </>
      )}
    </div>
  );
}